import React, { useMemo, useCallback, useState, useEffect, useRef, useLayoutEffect, lazy, Suspense } from 'react';
import styles from './GradeItemForCollection.module.scss';

import IconArrowUp from 'src/assets/icons/dropdown-white.svg?react';
import PDFIcon from "@assets/icons/wired-outline-pdf.svg?react";
import PlusIcon from "@assets/icons/wired-outline-plus.svg?react";
import { collectionTypeOptions, counselorCollectionScopeSequences, darkColors, IconStandalone } from '@src/constants';
import Button from '@src/components/button/Button';
import { LordIcon } from '@src/components/lord-icon/lord-icon';
import { getIconByTitle, getRandColor } from '@src/utils/helpers';
import CoverCollectionForProgram from '@src/components/CoverCollection/CoverCollectionForProgram';
import { CollectionLessonFragment, MultiCollectionContent, useGetCollectionLazyQuery, useGetMultiCollectionQuery } from '@src/api/graphql-codegen';
import CollectionDropDown from './CollectionDropDown/CollectionDropDown';
import LessonProgressModal from '@src/components/lesson-progress-modal/LessonProgressModal';
import NewCoverCard from './NewCoverCard/NewCoverCard';
import SkeletonCoverCard from '@src/components/SkeletonCoverCard/SkeletonCoverCard';
import { logger } from '../../../utils/logger';
import Spinner from '@src/components/spinner/Spinner';

const LazyCollection = lazy(() => import('@src/pages/transitions/gradeItem/LazyCollection/LazyCollection'));

type Props = {
  MCId: string;
  MCName: string;
  selectedMCId: string | null;
  isMCSelected: boolean;
  handleMCSelect: (program: any) => void;
  clickedAnywhere: boolean;
  handleBackToTop?: () => void;
  refresh?: () => void;
  handleSelectCollection?: () => void;
  programId?: string | undefined;
  programName?: string;
  MCGradeName: string;
  isAUuser: boolean;
  setLoadOthers: React.Dispatch<React.SetStateAction<number>>;
  loadOthers: number;
  index: number;
};

function GradeItemForMultiCollection(props: Props) {
  const {
    MCId,
    MCName,
    selectedMCId,
    isMCSelected,
    handleMCSelect,
    handleSelectCollection,
    programId,
    programName,
    MCGradeName,
    isAUuser,
    loadOthers = 0,
    setLoadOthers,
    index,
  } = props;

  useEffect(() => {
    const handleBeforeUnload = () => {
      // Set a flag in session storage to detect refresh
      sessionStorage.setItem('isGradeReloading', 'true');
    };
  
    window.addEventListener('beforeunload', handleBeforeUnload);
  
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const [currentCollectionId, setCurrentCollectionId] = useState('');
  const [activeIndex, setActiveIndex] = useState();
  const [currentLessonId, setCurrentLessonId] = useState('');
  const [showLesson, setShowLesson] = useState(false);
  const collectionDropDownRef = useRef<HTMLDivElement>(null);
  const [shouldLoad, setShouldLoad] = useState(index === 0 ? true : false);
  const scrollTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isFirstItem = index === 0;

  const checkIfInView = useCallback(() => {
    if (!wrapperRef.current) return;
  
    // Always load first item immediately
    if (isFirstItem) {
      setShouldLoad(true);
      setLoadOthers(index + 1);
      logger.debug('[Program] should load the first MC.')
      return;
    }

    const rect = wrapperRef.current.getBoundingClientRect();
    const inView = rect.top >= 0 && rect.bottom <= window.innerHeight;

    if (inView) {
      setShouldLoad(true); // Load this grade
      setLoadOthers(index + 1); // Queue the next grade for loading
    }
  }, [setLoadOthers, index, isFirstItem]);

  useEffect(() => {
    const handleScroll = () => {
      if (scrollTimeout.current) clearTimeout(scrollTimeout.current);

      scrollTimeout.current = setTimeout(() => {
        checkIfInView();
      }, 100); // Wait for 100ms after scroll stops
    };

    window.addEventListener('scroll', handleScroll);
    checkIfInView(); // Initial check
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
    };
  }, [checkIfInView]);

  useEffect(() => {
    if (loadOthers === index) {
      const loadTimer = setTimeout(() => {
        setShouldLoad(true);
        setLoadOthers(index + 1); // Queue the next grade
      }, 100);

      return () => clearTimeout(loadTimer);
    }
  }, [loadOthers, index, setLoadOthers]);

  const {data, loading} = useGetMultiCollectionQuery({
    variables: {input: {id: MCId}},
    fetchPolicy: 'network-only',
    skip: !shouldLoad,
  });

  const [getCollection, { data: currentCollection, loading: collectionLoading }] = useGetCollectionLazyQuery({ fetchPolicy: 'network-only' });

  const isCurrentMCSelected = useMemo(() => {
    return (isMCSelected && selectedMCId === MCId) ? true : false;
  }, [isMCSelected, selectedMCId, MCId]);

  const isIndividual = useMemo(() => {  // check collectionType Individual_Lesson_Image
    if (!data?.getMultiCollection?.content?.length) return false;
    return data?.getMultiCollection?.content.some(collection => collection?.collectionType === collectionTypeOptions[0].value)
  }, [data?.getMultiCollection?.content])

  const [chunkSize, setChunkSize] = useState(4);

  const calculateSize = useCallback(() => {
    if (window.innerWidth <= 1200 && window.innerWidth > 900) {
      setChunkSize(3);
    } else if (window.innerWidth <= 900) {
      setChunkSize(2);
    } else {
      setChunkSize(4);
    }
    
  }, []);

  useLayoutEffect(() => {
    window.addEventListener("resize", calculateSize);

    calculateSize();
    return () => {
      window.removeEventListener("resize", calculateSize);
    };
  }, [calculateSize]);

  const filteredContent = useMemo(() => {
    if (!data?.getMultiCollection?.content?.length) return []
    if (isIndividual) return [...data?.getMultiCollection?.content ?? []].map(collection => {
      const color = getRandColor(data?.getMultiCollection?.content ?? [], darkColors) // Add icon color for collection first character
      return {
        ...collection,
        color: color,
      }
    });
    const CHUNK_SIZE = chunkSize
    const result: any[] = [];
    for (let i = 0; i < data?.getMultiCollection?.content?.length; i += CHUNK_SIZE) {
      result.push(data?.getMultiCollection?.content?.slice(i, i + CHUNK_SIZE));
    }

    return result;
  }, [data?.getMultiCollection?.content, isIndividual, chunkSize])

  const Icon = getIconByTitle(MCName);

  const handleViewMCButton = () => {
    handleMCSelect(MCId)
    setCurrentCollectionId('');

    setTimeout(() => {
      const courseElement = document.getElementById(MCId);
      courseElement?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      })
    }, 500)
  }

  useEffect(() => {
    if (selectedMCId !== MCId) {
      setCurrentCollectionId('');
      setActiveIndex(undefined);
    }
  }, [selectedMCId, isMCSelected, MCId])

  useEffect(() => {
    if (currentCollectionId && collectionDropDownRef.current) {
      collectionDropDownRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [collectionDropDownRef, currentCollectionId]);

  const handleCollectionLessons = (e, id, index) => {
    e.preventDefault();
    e.stopPropagation();
    handleSelectCollection?.();
    if (currentCollectionId === id) {
      setCurrentCollectionId('');
      setActiveIndex(undefined);
    } else {
      setActiveIndex(index)
      if (MCId !== selectedMCId) {
        handleMCSelect(MCId)
      }
      getCollection({ variables: { input: { id: id } } });
      setCurrentCollectionId(id);
    }
  }

  const handleOpenLesson = useCallback((e: React.MouseEvent<HTMLDivElement>, content: CollectionLessonFragment) => {
    e.preventDefault();
    e.stopPropagation();
    setShowLesson(true);
    setCurrentLessonId(content.id);
  }, [])

  const handleCloseModal = () => {
    setShowLesson(false)
    setCurrentLessonId('');
  };

  const handleOpenContent = useCallback((content: CollectionLessonFragment) => {
    setShowLesson(true);
    setCurrentLessonId(content.id);
  }, [])

  const isOpenCollection = useCallback((row_index) => {
    if (isCurrentMCSelected && currentCollectionId && (row_index === activeIndex)) {
      return true
    }
    return false
  }, [isCurrentMCSelected, currentCollectionId, activeIndex])
  const isCollectionBlured = useCallback((collectionId) => {
    if (isMCSelected && (MCId === selectedMCId) && (currentCollectionId !== collectionId) && currentCollectionId !== '') {
      return true
    }
    return false
  }, [isMCSelected, currentCollectionId, selectedMCId, MCId])

  const handleClick = (e) => {
    setCurrentCollectionId('');
    setActiveIndex(undefined);
  }

  if (sessionStorage.getItem('isGradeReloading')) {
    sessionStorage.removeItem('isGradeReloading'); // Clear the flag after the reload
    return null;
  }

  return (
    <div id={MCId} className={styles.wrapper}>
      <div className={styles.backdrop} />
      <div className={styles.mcCard}>
        <div ref={wrapperRef} className={styles.gradeTitleView}>
          <div className={styles.gradeGrayView}>
            <div className={styles.titleSection}>
              <Icon />
              <h1>{MCName}</h1>
            </div>
            {counselorCollectionScopeSequences[MCName] && <div
              className={styles.scopePdfView}
              onClick={(e) => {
                e.preventDefault();
                window.open(
                  counselorCollectionScopeSequences[MCName],
                  "_blank"
                );
              }}
            >
              <PDFIcon />
              <span>Lesson Overviews</span>
              <PlusIcon />
            </div>}
          </div>
        </div>
        <div className={isIndividual ? styles.topLessonsView : styles.topUnitView} style={{ maxHeight: isCurrentMCSelected ? 'min-content' : isIndividual ? '680px' : '408px' }} onClick={handleClick}>
          <div className={styles.topUnitSubView}>
            {loading ? <div className={styles.skeletonLoadingView}>
              <div className={styles.skeletonView} >
              <SkeletonCoverCard isPopular={true} />
                <SkeletonCoverCard isPopular={true} />
                <SkeletonCoverCard isPopular={true} />
                <SkeletonCoverCard isPopular={true} />
              </div>
            </div> : <div className={styles.collectionsView}>
              {isIndividual ? <div className={styles.individualLessonsView}>
                <Suspense fallback={<Spinner />}>
                  {filteredContent.map((col, index) => {
                    return (
                      <LazyCollection key={index} collection={col} programId={programId} handleOpenContent={handleOpenContent} />
                    )
                  })}
                </Suspense>
                </div> :
                <div style={{width: '100%'}}>
                {filteredContent.map((row: any, row_index: number) => {
                  return (<>
                    <div key={`${row.id}-${row_index}`} className={styles.unitsDivWithFourGradient}>
                      {row.map((c: MultiCollectionContent, index: number) => {
                        if (c.type === 'Collection') {
                          return (
                            <CoverCollectionForProgram
                              key={index}
                              id={c.id}
                              isCollectionBlured={isCollectionBlured(c.id)}
                              name={c.name}
                              grade={c.grade ?? ''}
                              bannerImage={c.bannerImage ?? '/'}
                              bannerVideo={c.bannerVideo}
                              collectionType={c.collectionType ?? ''}
                              lessonCount={c.suggestedLessonCount ?? 0}
                              onCardClick={(e, id) => handleCollectionLessons(e, id, row_index)}
                            />
                          )
                        } else {
                          return (
                            <NewCoverCard
                              key={index}
                              content={c as any}
                              country={data?.getMultiCollection?.country ?? ''}
                              handleOpenContent={handleOpenLesson}
                              isAUuser={isAUuser}
                          />
                         )
                        }
                      })}
                    </div>
                    {row_index === activeIndex && <div ref={collectionDropDownRef}>
                      <CollectionDropDown
                        isSubHeading={currentCollection?.getCollection?.isSubHeading ?? false}
                        subHeading={currentCollection?.getCollection?.subHeading ?? ''}
                        subHeadingHighlight={currentCollection?.getCollection?.subHeadingHighlight ?? '[]'}
                        sllLessons={currentCollection?.getCollection?.sllLessons as any ?? []}
                        loading={collectionLoading}
                        isOpen={isOpenCollection(row_index)}
                        index={row_index}
                        id={`${MCId}_${row_index}`}
                        activeIndex={activeIndex}
                        handleOpenLesson={handleOpenLesson}
                        country={currentCollection?.getCollection?.country ?? ""}
                        isAUuser={isAUuser}
                      />
                    </div>}
                  </>)
                })}
                </div>
              }
              {filteredContent.length > 1 ? <div className={!isCurrentMCSelected ? styles.gradientBackground : ''}></div> : null}

            </div>}
          </div>
        </div>
        {data?.getMultiCollection?.content && data?.getMultiCollection?.content?.length > chunkSize ? <>
          {<div style={{ height: '30px' }}></div>}
          <div>
            <div className={styles.hoverTitleView}>
              <div className={styles.hoverTitleSubView}>
                <Button size='small' className={styles.extendViewButton} label={isCurrentMCSelected ? "View Less" : "View Lessons"}
                  leftIcon={<LordIcon size={30} src={IconStandalone} isHover={true} colors={{ primary: '#FFFFFF', secondary: '#FFFFFF' }} target='.itemWrapper__bMNcg' />}
                  rightIcon={<IconArrowUp className={[styles.arrowIcon, isCurrentMCSelected ? styles.materialToggleClose : styles.materialToggleOpen].join(' ')} width={13} />} onClick={handleViewMCButton} />
              </div>
            </div>
          </div>
        </> : <>
          {isIndividual ? 
          <div>
            <div style={{ height: '30px' }}></div>
            <div className={styles.hoverTitleView}>
              <div className={styles.hoverTitleSubView}>
                <Button size='small' className={styles.extendViewButton} label={isCurrentMCSelected ? "View Less" : "View Lessons"}
                  leftIcon={<LordIcon size={30} src={IconStandalone} isHover={true} colors={{ primary: '#FFFFFF', secondary: '#FFFFFF' }} target='.itemWrapper__bMNcg' />}
                  rightIcon={<IconArrowUp className={[styles.arrowIcon, isCurrentMCSelected ? styles.materialToggleClose : styles.materialToggleOpen].join(' ')} width={13} />} onClick={handleViewMCButton} />
              </div>
            </div>
          </div> : null}
        </>}
      </div>
      {currentLessonId && showLesson ? 
        <LessonProgressModal
          open={showLesson}
          onClose={handleCloseModal}
          lessonId={currentLessonId}
          gradeName={currentCollection?.getCollection?.grade ?? MCGradeName}
          mcId={MCId} mcName={MCName}
          launchedGrade={data?.getMultiCollection?.grade ?? MCGradeName}
          collectionId={currentCollectionId}
          collectionName={currentCollection?.getCollection?.name ?? ''}
          programId={programId}
          programName={programName}
        />
        : null}
    </div>
  );
}

export default GradeItemForMultiCollection;
